import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { service } from "@ember/service";
import { fn } from "@ember/helper";
import { on } from "@ember/modifier";
import { t } from "ember-intl";
import Box from "glesys-controlpanel/components/box";
import Table from "glesys-controlpanel/components/table";
import TableTd from "glesys-controlpanel/components/table/td";
import SecureContent from "glesys-controlpanel/components/secure-content";
import Dropdown from "glesys-controlpanel/components/dropdown";
import DropdownItem from "glesys-controlpanel/components/dropdown/item";
import DataLoader from "glesys-controlpanel/components/data-loader";
import OverlayLoadingSpinner from "glesys-controlpanel/components/overlay-loading-spinner";
import canManage from "glesys-controlpanel/helpers/can-manage";

const MASKED_PASSWORD = "***********";
const DEFAULT_USERNAME = "dbadmin";
const DEFAULT_DATABASE = "defaultdb";

const maskedConnectionString = (connectionString) => {
  let details = new URL(connectionString);

  details.password = MASKED_PASSWORD;

  return details?.toString();
};

export default class ConnectionDetails extends Component {
  @service intl;

  @tracked selectedUser = null;
  @tracked selectedDatabase = null;

  constructor() {
    super(...arguments);

    this.selectedUser =
      this.instanceUsers.find((user) => user.username === DEFAULT_USERNAME) ?? this.instanceUsers.at(0) ?? null;
    this.selectedDatabase = this.defaultDatabase;
  }

  get loadConnectionDetails() {
    let username = this.selectedUser?.username;
    let databaseName = this.selectedDatabase?.name ?? DEFAULT_DATABASE;

    return () => this.args.fetchConnectionDetails.perform(username, databaseName);
  }

  get instanceUsers() {
    return this.args.database.instanceUsers ?? [];
  }

  get instanceDatabases() {
    return this.args.database.instanceDatabases ?? [];
  }

  get showBuilder() {
    return this.instanceUsers.length > 1 || this.instanceDatabases.length > 1;
  }

  get accessibleDatabases() {
    if (!this.selectedUser) {
      return [];
    }

    let grantedIds = (this.selectedUser.permissions ?? [])
      .filter((permission) => permission.permission === "READ" || permission.permission === "WRITE")
      .map((permission) => permission.databaseid);

    return this.instanceDatabases.filter((database) => grantedIds.includes(database.id));
  }

  get defaultDatabase() {
    return (
      this.accessibleDatabases.find((database) => database.name === DEFAULT_DATABASE) ??
      this.accessibleDatabases.at(0) ??
      null
    );
  }

  get noAccessibleDatabase() {
    return Boolean(this.selectedUser) && this.accessibleDatabases.length === 0;
  }

  get selectedUserLabel() {
    return this.selectedUser?.username;
  }

  get selectedDatabaseLabel() {
    if (this.noAccessibleDatabase) {
      return this.intl.t("databases.details.connection.builder.no-permissions");
    }

    return this.selectedDatabase?.name ?? this.intl.t("databases.details.connection.builder.select-database");
  }

  pickUser = (user) => {
    this.selectedUser = user;

    if (!this.accessibleDatabases.includes(this.selectedDatabase)) {
      this.selectedDatabase = this.defaultDatabase;
    }
  };

  pickDatabase = (database) => {
    this.selectedDatabase = database;
  };

  tableData = (details) => {
    if (!details) {
      return [];
    }

    const { username, password, pathname } = details;

    return [
      { key: this.intl.t("databases.details.connection.table.hostname"), value: this.args.database.fqdn },
      { key: this.intl.t("databases.details.connection.table.username"), value: username },
      {
        key: this.intl.t("databases.details.connection.table.password"),
        value: decodeURIComponent(password),
        maskedValue: MASKED_PASSWORD,
      },
      ...(this.noAccessibleDatabase
        ? []
        : [{ key: this.intl.t("databases.details.connection.table.database-name"), value: pathname.slice(1) }]),
      {
        key: this.intl.t("databases.details.connection.table.ssl-mode"),
        value: this.intl.t("databases.details.connection.table.required"),
      },
      ...(this.noAccessibleDatabase
        ? []
        : [
            {
              key: this.intl.t("databases.details.connection.table.connection-string"),
              value: details.toString(),
              maskedValue: maskedConnectionString(details.toString()),
            },
          ]),
    ];
  };

  <template>
    <Box data-test-database-details-connecting>
      <:header>{{t "label.connecting"}}</:header>
      <:default>
        {{#if (canManage)}}
          <span class="text-gray-500 text-base">{{t "databases.details.connection.info"}}</span>

          {{#if this.showBuilder}}
            <div class="mt-8" role="group" aria-label={{t "databases.details.connection.builder.heading"}}>
              <span class="mb-2 block text-sm font-normal text-gray-700">
                {{t "databases.details.connection.builder.heading"}}
              </span>
              <div class="flex flex-wrap gap-4">
                <Dropdown
                  class="border border-gray-300 rounded-sm px-3 py-2 text-sm"
                  data-test-database-connection-user-menu
                >
                  <:header>
                    <span class="sr-only">{{t "databases.details.connection.builder.select-user"}}</span>
                    {{this.selectedUserLabel}}
                  </:header>
                  <:default>
                    {{#each this.instanceUsers as |user|}}
                      <DropdownItem
                        data-test-database-connection-user={{user.username}}
                        {{on "click" (fn this.pickUser user)}}
                      >
                        {{user.username}}
                      </DropdownItem>
                    {{/each}}
                  </:default>
                </Dropdown>
                <Dropdown
                  class="border border-gray-300 rounded-sm px-3 py-2 text-sm"
                  data-test-database-connection-database-menu
                  disabled={{this.noAccessibleDatabase}}
                >
                  <:header>
                    <span class="sr-only">{{t "databases.details.connection.builder.select-database"}}</span>
                    {{this.selectedDatabaseLabel}}
                  </:header>
                  <:default>
                    {{#each this.accessibleDatabases as |database|}}
                      <DropdownItem
                        data-test-database-connection-database={{database.name}}
                        {{on "click" (fn this.pickDatabase database)}}
                      >
                        {{database.name}}
                      </DropdownItem>
                    {{/each}}
                  </:default>
                </Dropdown>
              </div>
              <p class="sr-only" aria-live="polite">
                {{#if this.noAccessibleDatabase}}
                  {{t "databases.details.connection.builder.no-permissions-hint"}}
                {{/if}}
              </p>
            </div>
          {{/if}}

          <DataLoader @load={{this.loadConnectionDetails}} @useLastSuccessfulValue={{true}}>
            <:error>
              <p class="text-red-600 mt-8" data-test-database-connection-error>
                {{t "databases.details.connection.error"}}
              </p>
            </:error>
            <:default as |details isLoading|>
              {{#if details}}
                <OverlayLoadingSpinner @isLoading={{isLoading}}>
                  <Table
                    class="mx-[-24px] mb-[-25px] mt-8"
                    @items={{this.tableData details}}
                    @footer={{false}}
                    @outsideBorders={{false}}
                  >
                    <:body as |item|>
                      <TableTd class="w-1/6 text-gray-600">{{item.key}}</TableTd>
                      <TableTd class="w-5/6">
                        {{#if item.maskedValue}}
                          <SecureContent>
                            <:default>{{item.value}}</:default>
                            <:hidden>{{item.maskedValue}}</:hidden>
                          </SecureContent>
                        {{else}}
                          {{item.value}}
                        {{/if}}
                      </TableTd>
                    </:body>
                  </Table>
                </OverlayLoadingSpinner>
              {{/if}}
            </:default>
          </DataLoader>
        {{else}}
          <span class="text-gray-600" data-test-database-details-readonly-information>
            {{t "databases.details.connection.readonly-information"}}
          </span>
        {{/if}}
      </:default>
    </Box>
  </template>
}
